// external imports
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { CloudUploadOutlined, Close } from '@mui/icons-material';

// internal imports
import { FileTypeBadge, formatBytes } from './common';

const ACCEPTED = '.xlsx,.xls,.pdf,.jpg,.jpeg,.png';

/**
 * Upload + submit a translated file for one assignment.
 * onSubmit(file) → parent POSTs to /task/assignment/:id/submit (multipart, field "file").
 */
const SubmitTranslationModal = ({ open, assignment, submitting, error, onClose, onSubmit }) => {
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [localError, setLocalError] = React.useState('');
  const fileInputRef = React.useRef(null);

  // reset each time the dialog opens (or targets a different assignment)
  React.useEffect(() => {
    if (open) {
      setFile(null);
      setLocalError('');
      setDragOver(false);
    }
  }, [open, assignment?._id]);

  const pickFile = (fileList) => {
    const first = fileList && fileList[0];
    if (first) {
      setFile(first);
      setLocalError('');
    }
  };

  const handleSubmit = () => {
    if (!file) {
      setLocalError('A translated file is required.');
      return;
    }
    onSubmit(file);
  };

  const isRedo = assignment?.status === 'rejected';

  return (
    <Dialog
      open={!!open}
      onClose={() => (submitting ? null : onClose())}
      PaperProps={{ sx: { borderRadius: '16px', width: '100%', maxWidth: 480 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 0.5 }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
            {isRedo ? 'Re-upload' : 'Submit'} {assignment?.languageName} translation
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>
            {assignment?.task?.taskName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <Close sx={{ fontSize: 20, color: '#94a3b8' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isRedo && assignment?.reviewRemark && (
          <Box
            sx={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '10px',
              px: 1.5,
              py: 1,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#b91c1c', mb: 0.25 }}>
              Sent back for changes
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#b91c1c', fontStyle: 'italic' }}>
              “{assignment.reviewRemark}”
            </Typography>
          </Box>
        )}

        {/* dropzone */}
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pickFile(e.dataTransfer.files);
          }}
          sx={{
            border: `1.5px dashed ${dragOver ? '#6366f1' : '#cbd5e1'}`,
            borderRadius: '12px',
            background: dragOver ? '#eef2ff' : '#f8fafc',
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:hover': { borderColor: '#6366f1', background: '#eef2ff' },
          }}
        >
          <CloudUploadOutlined sx={{ color: '#94a3b8', fontSize: 28, mb: 0.5 }} />
          <Typography sx={{ fontSize: 14, color: '#64748b' }}>
            Drop file here or <span style={{ color: '#4f46e5', fontWeight: 600 }}>browse</span> to
            upload
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            hidden
            onChange={(e) => {
              pickFile(e.target.files);
              e.target.value = '';
            }}
          />
        </Box>

        {/* selected file */}
        {file && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              p: 1,
              pr: 1.5,
              mt: 1.5,
              background: '#fff',
            }}
          >
            <FileTypeBadge fileType="" fileName={file.name} size={40} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1e293b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                {formatBytes(file.size)}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setFile(null)} disabled={submitting}>
              <Close sx={{ fontSize: 18, color: '#94a3b8' }} />
            </IconButton>
          </Box>
        )}

        {(localError || error) && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '10px', py: 0.25 }}>
            {localError || error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            borderColor: '#e2e8f0',
            color: '#475569',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '10px',
            px: 3,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
          }}
        >
          {submitting ? 'Submitting…' : 'Submit Translation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitTranslationModal;
